import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { TournamentTeamService } from '@core/services/tournament-team.service';
import { combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { componentDestroyed, handleError, StatisticFilters, Team, TeamUserRole, TournamentTeam, UntilDestroy } from '@mtgame/core';
import { SeoService } from '@core/services/seo.service';
import { LeagueRootService } from '@core/services/league-root.service';
import { isPlatformBrowser } from '@angular/common';
import { LoaderService, ToastService } from '@mtgame/ui';

@UntilDestroy
@Component({
  selector: 'mtg-tournament-team',
  templateUrl: './tournament-team.component.html',
  styleUrls: ['./tournament-team.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TournamentTeamComponent implements OnInit, OnDestroy {
  league = this.leagueRootService.getLeagueModel();
  team: Team;
  tournamentTeam: TournamentTeam;
  usersCount: number;
  isBrowser = isPlatformBrowser(this.platformId);
  actualTournamentTeam: TournamentTeam;

  get lastRoundDefault(): boolean {
    return this.team.sport.isHockey();
  }

  get filters(): Partial<StatisticFilters> {
    return this.tournamentTeamService.filters;
  }

  constructor(
    @Inject(PLATFORM_ID) private platformId: string,
    private leagueRootService: LeagueRootService,
    private tournamentTeamService: TournamentTeamService,
    private cdr: ChangeDetectorRef,
    private seoService: SeoService,
    private toastService: ToastService,
    private loaderService: LoaderService,
  ) {
  }

  ngOnInit(): void {
    combineLatest([
      this.tournamentTeamService.team$.pipe(takeUntil(componentDestroyed(this))),
      this.tournamentTeamService.tournamentTeam$.pipe(takeUntil(componentDestroyed(this))),
    ]).subscribe(([team, tournamentTeam]) => {
      if (!team || !tournamentTeam) {
        return;
      }
      this.team = team;
      this.tournamentTeam = tournamentTeam;
      this.seoService.setData({
        title: `Команда ${team.name}`
      });
      this.cdr.markForCheck();
    });
    this.tournamentTeamService.users$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(users => {
        const staffRoles = [TeamUserRole.manager, TeamUserRole.head_manager, TeamUserRole.coach, TeamUserRole.head_coach,
        TeamUserRole.senior_coach, TeamUserRole.assistant_coach, TeamUserRole.serviceman, TeamUserRole.executive, TeamUserRole.press_attache, TeamUserRole.vice_president, TeamUserRole.president];

        this.usersCount = users.filter(item => staffRoles.indexOf(item.teamUser.role) < 0).length;
        this.cdr.markForCheck();
      });
    this.tournamentTeamService.tournamentTeams$
      .pipe(takeUntil(componentDestroyed(this)))
      .subscribe(teams => {
        const sortedTeams = teams.sort((a, b) => {
          if (a.tournament.season.id === b.tournament.season.id) {
            return a.tournament.date > b.tournament.date
              ? -1
              : 1;
          }
          return a.tournament.season.id > b.tournament.season.id
            ? -1
            : 1;
        });
        this.actualTournamentTeam = sortedTeams.length > 0 ? sortedTeams[0] : null;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.seoService.reset();
    this.tournamentTeamService.dispose();
  }

  async setFilters(filters: Partial<StatisticFilters>): Promise<void> {
    this.loaderService.show();
    try {
      await this.tournamentTeamService.setFilter(filters);
    } catch (e) {
      handleError(e);
      this.toastService.danger('Произошла ошибка');
    } finally {
      this.loaderService.hide();
    }
  }

  get commonStatistic$() {
    return this.tournamentTeamService.commonStatistic$;
  }
}
